import type { ReactNode } from 'react';
import { cx } from '@/lib/cx';
import type { Sign } from '@/lib/format';
import s from './Stats.module.css';

export interface StatItem {
  label: ReactNode;
  value: ReactNode;
  note?: ReactNode;
  /** Colours the value by sign: gains green, losses red. */
  sign?: Sign;
  /** Forces the loss colour (drawdowns are always losses). */
  negative?: boolean;
  title?: string;
}

/** The headline strip of figures (6 across on wide screens). */
export function StatGrid({ items, columns = 6, className }: { items: StatItem[]; columns?: number; className?: string }) {
  return (
    <div className={cx(s.grid, className)} style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
      {items.map((it, i) => (
        <div key={i} className={s.cell} title={it.title}>
          <span className={s.label}>{it.label}</span>
          <span className={cx(s.value, toneClass(it))}>{it.value}</span>
          {it.note && <span className={s.note}>{it.note}</span>}
        </div>
      ))}
    </div>
  );
}

function toneClass(it: StatItem) {
  if (it.negative) return s.neg;
  if (it.sign === 'pos') return s.pos;
  if (it.sign === 'neg') return s.neg;
  return undefined;
}

/** A compact figure on a sunken tile (trade explorer facts, run summaries). */
export function FactTile({ label, value, sign, negative }: StatItem) {
  return (
    <div className={s.fact}>
      <span className={s.factLabel}>{label}</span>
      <span className={cx(s.factValue, toneClass({ label, value, sign, negative }))}>{value}</span>
    </div>
  );
}

export function FactGrid({ children, columns = 2 }: { children: ReactNode; columns?: number }) {
  return (
    <div className={s.factGrid} style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
      {children}
    </div>
  );
}

/** A big single figure with a unit (Today's split view, credit balance). */
export function BigNumber({ value, unit, sign }: { value: ReactNode; unit?: ReactNode; sign?: Sign }) {
  return (
    <span className={s.big}>
      <span className={cx(s.bigValue, sign === 'pos' && s.pos, sign === 'neg' && s.neg)}>{value}</span>
      {unit && <span className={s.bigUnit}>{unit}</span>}
    </span>
  );
}

/** Label/value rows (scenario details, account fields). */
export function KeyValueList({ rows }: { rows: Array<{ label: ReactNode; value: ReactNode; mono?: boolean }> }) {
  return (
    <dl className={s.kv}>
      {rows.map((r, i) => (
        <div key={i} className={s.kvRow}>
          <dt>{r.label}</dt>
          <dd className={r.mono ? 'mono' : undefined}>{r.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** A small uppercase heading inside a panel or card. */
export function SectionLabel({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div className={s.section}>
      <span>{children}</span>
      {aside && <span className={s.sectionAside}>{aside}</span>}
    </div>
  );
}
